/** Тосты: единая очередь уведомлений с озвучкой через aria-live. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastKind = 'info' | 'success' | 'danger';

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  text?: string;
  /** Действие в тосте — например «Пересчитать». */
  action?: { label: string; run: () => void };
}

export interface ToastApi {
  push(toast: Omit<Toast, 'id'>): number;
  dismiss(id: number): void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TTL_MS: Record<ToastKind, number> = {
  info: 4_000,
  success: 4_000,
  danger: 9_000,
};

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++;
      // Больше трёх тостов на мобильном экране читать невозможно.
      setToasts((list) => [...list.slice(-2), { ...toast, id }]);
      timers.current.set(
        id,
        window.setTimeout(() => dismiss(id), TTL_MS[toast.kind]),
      );
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) window.clearTimeout(timer);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="region" aria-label="Уведомления">
        <div className="toasts__live" aria-live="polite" aria-atomic="false">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`toast toast--${toast.kind}`}
              role={toast.kind === 'danger' ? 'alert' : 'status'}
            >
              <div className="toast__body">
                <p className="toast__title">{toast.title}</p>
                {toast.text ? <p className="toast__text">{toast.text}</p> : null}
              </div>
              {toast.action ? (
                <button
                  type="button"
                  className="toast__action"
                  onClick={() => {
                    toast.action?.run();
                    dismiss(toast.id);
                  }}
                >
                  {toast.action.label}
                </button>
              ) : (
                <button
                  type="button"
                  className="toast__close"
                  aria-label="Закрыть уведомление"
                  onClick={() => dismiss(toast.id)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToasts(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToasts вызван вне ToastProvider');
  return context;
}
